/**
 * ProviderVerificationDashboard - Dashboard for providers to manage verification
 * Features: Submit documents, view verification status, verification tiers
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Shield, CheckCircle, Clock, Upload, FileText, Award,
  AlertTriangle, ChevronRight, Loader2, Star, Building,
  CreditCard, BadgeCheck, Camera, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification Levels
const VERIFICATION_LEVELS = [
  {
    level: 0,
    name: 'Unverified',
    description: 'Submit documents to get verified',
    color: 'bg-gray-100 text-gray-600',
    icon: Shield,
  },
  {
    level: 1,
    name: 'ID Verified',
    description: 'Basic identity verification complete',
    color: 'bg-blue-100 text-blue-600',
    icon: BadgeCheck,
    benefits: ['Display "Verified" badge', 'Priority in search results'],
  },
  {
    level: 2,
    name: 'Professional',
    description: 'Professional credentials verified',
    color: 'bg-purple-100 text-purple-600',
    icon: Award,
    benefits: ['All Level 1 benefits', '"Professional" badge', 'Featured in category pages'],
  },
  {
    level: 3,
    name: 'Premium Provider',
    description: 'Fully verified with insurance',
    color: 'bg-amber-100 text-amber-600',
    icon: Star,
    benefits: ['All Level 2 benefits', '"Premium" badge', 'Top placement', 'Priority customer support'],
  },
];

// Document Types
const DOCUMENT_TYPES = [
  { id: 'id_proof', name: 'ID Proof', description: 'Passport, driving license, or national ID', required: true },
  { id: 'business_license', name: 'Business License', description: 'Business registration or trade license', required: false },
  { id: 'qualifications', name: 'Professional Qualifications', description: 'Certificates, diplomas, or degrees', required: false },
  { id: 'insurance', name: 'Insurance Certificate', description: 'Professional liability insurance', required: false },
  { id: 'vet_license', name: 'Veterinary License', description: 'Valid veterinary practice license', required: false },
];

// Level Progress Component
function LevelProgress({ currentLevel }) {
  const progress = (currentLevel / 3) * 100;
  
  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">Your Verification Level</h3>
            <p className="text-sm text-gray-500">
              {VERIFICATION_LEVELS[currentLevel]?.name || 'Unverified'}
            </p>
          </div>
          <Badge className={VERIFICATION_LEVELS[currentLevel]?.color}>
            Level {currentLevel}
          </Badge>
        </div>
        
        <Progress value={progress} className="h-2 mb-4" />
        
        <div className="flex justify-between text-xs text-gray-500">
          {VERIFICATION_LEVELS.map((level) => (
            <div 
              key={level.level}
              className={`flex flex-col items-center ${currentLevel >= level.level ? 'text-indigo-600' : ''}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                currentLevel >= level.level ? 'bg-indigo-600 text-white' : 'bg-gray-200'
              }`}>
                {currentLevel >= level.level ? <CheckCircle className="w-4 h-4" /> : level.level}
              </div>
              <span className="hidden sm:block">{level.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Level Benefits Card
function LevelBenefitsCard({ level, isCurrentLevel, isUnlocked }) {
  const config = VERIFICATION_LEVELS[level];
  const Icon = config.icon;
  
  return (
    <Card className={`${isCurrentLevel ? 'ring-2 ring-indigo-500' : ''} ${!isUnlocked ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold">{config.name}</h4>
              {isCurrentLevel && <Badge variant="outline" className="text-xs">Current</Badge>}
              {isUnlocked && !isCurrentLevel && <CheckCircle className="w-4 h-4 text-green-500" />}
            </div>
            <p className="text-sm text-gray-500">{config.description}</p>
            {config.benefits && (
              <ul className="mt-2 space-y-1">
                {config.benefits.map((benefit, idx) => (
                  <li key={idx} className="text-xs text-gray-600 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3 text-indigo-500" />
                    {benefit}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Document Upload Component
function DocumentUpload({ docType, uploadedDoc, onUpload, onRemove }) {
  const [uploading, setUploading] = useState(false);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // In production, this would upload to cloud storage
    // For now, we'll simulate with a fake URL
    setUploading(true);
    
    setTimeout(() => {
      onUpload({
        document_type: docType.id,
        document_url: `https://storage.example.com/${file.name}`,
        document_name: file.name,
      });
      setUploading(false);
      toast.success(`${docType.name} uploaded`);
    }, 1500);
  };
  
  return (
    <div className={`p-4 border rounded-lg ${uploadedDoc ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <FileText className={`w-5 h-5 mt-0.5 ${uploadedDoc ? 'text-green-600' : 'text-gray-400'}`} />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{docType.name}</span>
              {docType.required && <Badge variant="outline" className="text-xs">Required</Badge>}
            </div>
            <p className="text-sm text-gray-500">{docType.description}</p>
          </div>
        </div>
        
        {uploadedDoc ? (
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Uploaded
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => onRemove(docType.id)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label className="cursor-pointer">
            <input 
              type="file" 
              className="hidden" 
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <Button size="sm" variant="outline" disabled={uploading} asChild>
              <span>
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-1" />
                )}
                Upload
              </span>
            </Button>
          </label>
        )}
      </div>
      
      {uploadedDoc && (
        <div className="mt-2 pl-8">
          <p className="text-xs text-gray-500">{uploadedDoc.document_name}</p>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function ProviderVerificationDashboard() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [notes, setNotes] = useState('');
  
  // Mock provider ID - in production, get from auth context
  const providerId = localStorage.getItem('providerId') || 'provider-123';
  
  useEffect(() => {
    // Simulate loading provider verification status
    setTimeout(() => {
      setCurrentLevel(0); // Default to unverified
      setLoading(false);
    }, 1000);
  }, []);
  
  const handleDocUpload = (doc) => {
    setUploadedDocs(prev => ({
      ...prev,
      [doc.document_type]: doc,
    }));
  };
  
  const handleDocRemove = (docType) => {
    setUploadedDocs(prev => {
      const updated = { ...prev };
      delete updated[docType];
      return updated;
    });
  };
  
  const handleSubmitVerification = async () => {
    const documents = Object.values(uploadedDocs);
    
    if (documents.length === 0) {
      toast.error('Please upload at least one document');
      return;
    }
    
    // Check required documents
    const hasIdProof = uploadedDocs['id_proof'];
    if (!hasIdProof) {
      toast.error('ID Proof is required for verification');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/pets/provider/submit-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: providerId,
          documents: documents,
          notes: notes,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Verification submitted successfully!');
        setVerificationStatus('PENDING');
      } else {
        toast.error(data.message || 'Failed to submit verification');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="w-6 h-6" />
            Provider Verification
          </h1>
          <p className="text-indigo-200">Get verified to unlock premium features and increase trust</p>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Pending Status Banner */}
        {verificationStatus === 'PENDING' && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="w-6 h-6 text-amber-600" />
              <div>
                <h4 className="font-semibold text-amber-800">Verification In Progress</h4>
                <p className="text-sm text-amber-700">
                  Your documents are being reviewed. This usually takes 1-2 business days.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Level Progress */}
        <LevelProgress currentLevel={currentLevel} />
        
        {/* Verification Levels */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Verification Tiers</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {VERIFICATION_LEVELS.slice(1).map((level) => (
              <LevelBenefitsCard 
                key={level.level}
                level={level.level}
                isCurrentLevel={currentLevel === level.level}
                isUnlocked={currentLevel >= level.level}
              />
            ))}
          </div>
        </div>
        
        {/* Document Upload Section */}
        {verificationStatus !== 'PENDING' && (
          <Card>
            <CardHeader>
              <CardTitle>Submit Documents</CardTitle>
              <CardDescription>
                Upload the required documents to verify your profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {DOCUMENT_TYPES.map((docType) => (
                <DocumentUpload 
                  key={docType.id}
                  docType={docType}
                  uploadedDoc={uploadedDocs[docType.id]}
                  onUpload={handleDocUpload}
                  onRemove={handleDocRemove}
                />
              ))}
              
              <div className="pt-4 border-t">
                <Label>Additional Notes (optional)</Label>
                <Textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information about your credentials..."
                  rows={3}
                  className="mt-2"
                />
              </div>
              
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-indigo-600 mt-0.5" />
                  <div className="text-sm text-indigo-800">
                    <p className="font-medium">Document Guidelines</p>
                    <ul className="mt-1 space-y-1 text-indigo-700">
                      <li>• All documents must be clear and legible</li>
                      <li>• ID must be valid and not expired</li>
                      <li>• Professional certificates must show your name</li>
                      <li>• Accepted formats: PDF, JPG, PNG (max 5MB)</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600"
                onClick={handleSubmitVerification}
                disabled={submitting || Object.keys(uploadedDocs).length === 0}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Submit for Verification
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
